import { Component } from '@theme/component';
import { startViewTransition, isClickedOutside } from '@theme/utilities';

/**
 * Community Feature lightbox.
 *
 * Clicking a frame opens it, enlarged, in a native <dialog> — the same
 * Polaroid frame the wall uses, flattened and centered, not a bare
 * full-screen image. When the View Transitions API is available (and motion
 * isn't reduced) the clicked frame morphs into the enlarged one; otherwise
 * the dialog simply appears. The dialog's own `::backdrop` (shared with
 * every other dialog in the theme) dims the page behind it.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLImageElement} dialogImage
 * @property {HTMLElement} dialogCaption
 *
 * @extends Component<Refs>
 */
export class CommunityFeatureLightbox extends Component {
  requiredRefs = ['dialog', 'dialogImage', 'dialogCaption'];

  static transitionName = 'community-feature-item';

  /** @type {HTMLElement | null} The small card frame the open dialog came from. */
  #activeSourceFrame = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('click', this.#handleDialogClick);
    this.refs.dialog.addEventListener('keydown', this.#handleKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('click', this.#handleDialogClick);
    this.refs.dialog.removeEventListener('keydown', this.#handleKeyDown);
  }

  /**
   * Closes the dialog when the user clicks the backdrop.
   *
   * @param {MouseEvent} event
   */
  #handleDialogClick = (event) => {
    if (isClickedOutside(event, this.refs.dialog)) this.close(event);
  };

  /**
   * Closes the dialog on Escape through the same animated path as the close
   * button (the browser's own cancel/close would otherwise skip the
   * transition).
   *
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.close(event);
  };

  /**
   * Opens the lightbox for the clicked frame. The view-transition-name is set
   * on the frame itself (padded card, shadow and all) on both ends, so the
   * card morphs into its enlarged self rather than dissolving into a bare
   * image.
   *
   * @param {PointerEvent} event
   */
  open(event) {
    event.preventDefault();

    const trigger = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('.community-feature__frame-trigger') : null
    );
    if (!trigger) return;

    const sourceFrame = trigger.querySelector('.community-feature__frame');
    const targetFrame = this.querySelector('.community-feature__frame--expanded');
    if (!(sourceFrame instanceof HTMLElement) || !(targetFrame instanceof HTMLElement)) return;

    const { dialog, dialogImage, dialogCaption } = this.refs;

    const thumbnail = trigger.querySelector('img');
    dialogImage.src = trigger.dataset.fullImage || thumbnail?.currentSrc || thumbnail?.src || '';
    dialogImage.alt = trigger.dataset.alt || '';
    dialogCaption.textContent = trigger.dataset.caption || '';

    this.#activeSourceFrame = sourceFrame;

    const { transitionName } = CommunityFeatureLightbox;
    sourceFrame.style.setProperty('view-transition-name', transitionName);

    startViewTransition(() => {
      dialog.showModal();
      sourceFrame.style.removeProperty('view-transition-name');
      targetFrame.style.setProperty('view-transition-name', transitionName);
    }).finally(() => {
      targetFrame.style.removeProperty('view-transition-name');
    });
  }

  /**
   * Closes the lightbox, morphing the enlarged frame back into the card it
   * came from (falling back to a plain close if that card is no longer on
   * the page, e.g. after a section re-render).
   *
   * @param {Event} [event]
   */
  close(event) {
    event?.preventDefault();

    const { dialog } = this.refs;
    if (!dialog.open) return;

    const targetFrame = this.querySelector('.community-feature__frame--expanded');
    const sourceFrame = this.#activeSourceFrame;
    this.#activeSourceFrame = null;

    if (!(targetFrame instanceof HTMLElement)) return dialog.close();

    const { transitionName } = CommunityFeatureLightbox;
    targetFrame.style.setProperty('view-transition-name', transitionName);

    startViewTransition(() => {
      dialog.close();
      targetFrame.style.removeProperty('view-transition-name');
      sourceFrame?.style.setProperty('view-transition-name', transitionName);
    }).finally(() => {
      sourceFrame?.style.removeProperty('view-transition-name');
    });
  }
}

if (!customElements.get('community-feature-lightbox')) {
  customElements.define('community-feature-lightbox', CommunityFeatureLightbox);
}
